'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { TurnstileWidget } from '@/components/shared/TurnstileWidget';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { dealerInquirySchema, type DealerInquiryInput } from '@/lib/validation/auth';

interface RegisterFormProps {
  /** Cloudflare Turnstile site key. When null, no widget is rendered and
   *  the submit POSTs without a captcha token (dev / pre-Turnstile
   *  environments). The server-side route handles missing-token + missing-
   *  secret cases independently. */
  turnstileSiteKey: string | null;
}

export default function RegisterForm({ turnstileSiteKey }: RegisterFormProps) {
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [submitFailed, setSubmitFailed] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DealerInquiryInput>({ resolver: zodResolver(dealerInquirySchema) });

  const captchaRequired = turnstileSiteKey !== null;
  // Once a submit has failed we keep the form locked. Auto-resetting the
  // captcha widget would let Turnstile silently re-execute and
  // repopulate the token without user interaction — that lets a script
  // loop replay against our rate limit. Force a page refresh instead.
  const submitDisabled = isSubmitting || submitFailed || (captchaRequired && !captchaToken);

  function failedSubmission(message: string) {
    setServerError(message);
    setCaptchaToken('');
    setSubmitFailed(true);
  }

  async function onSubmit(values: DealerInquiryInput) {
    // Belt-and-braces — the submit button is already disabled when
    // submitFailed, but a programmatic form.requestSubmit() would
    // bypass the UI. Match the gate on the handler side too.
    if (submitFailed) return;
    setServerError(null);
    const r = await fetch('/api/dealer-inquiry', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...values, captchaToken: captchaToken || undefined }),
    });
    if (!r.ok) {
      const body = (await r.json().catch(() => ({}))) as { error?: string };
      if (r.status === 429) {
        failedSubmission(
          'Too many requests from this network. Please wait a few minutes, refresh the page, and try again — or email sales@linkimaging.com directly.',
        );
        return;
      }
      failedSubmission(
        `${body.error ?? 'Something went wrong.'} Please refresh the page to try again, or email us directly.`,
      );
      return;
    }
    setDone(true);
  }

  if (done) {
    return (
      <div className="space-y-2 text-sm">
        <p className="font-medium">Thanks — your request is in.</p>
        <p className="text-neutral-600">
          A member of the Link Imaging team will follow up shortly. If you don&rsquo;t hear back
          within a business day, email{' '}
          <a className="underline" href="mailto:sales@linkimaging.com">
            sales@linkimaging.com
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <div>
        <Label htmlFor="companyName">Company name</Label>
        <Input id="companyName" autoComplete="organization" {...register('companyName')} />
        {errors.companyName && <p className="text-xs text-red-600">{errors.companyName.message}</p>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label htmlFor="firstName">First name</Label>
          <Input id="firstName" autoComplete="given-name" {...register('firstName')} />
          {errors.firstName && <p className="text-xs text-red-600">{errors.firstName.message}</p>}
        </div>
        <div>
          <Label htmlFor="lastName">Last name</Label>
          <Input id="lastName" autoComplete="family-name" {...register('lastName')} />
          {errors.lastName && <p className="text-xs text-red-600">{errors.lastName.message}</p>}
        </div>
      </div>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" autoComplete="email" {...register('email')} />
        {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}
      </div>
      <div>
        <Label htmlFor="phone">
          Phone <span className="text-neutral-400">(optional)</span>
        </Label>
        <Input id="phone" type="tel" autoComplete="tel" {...register('phone')} />
      </div>
      <div>
        <Label htmlFor="accountNum">
          Existing account # <span className="text-neutral-400">(if you have one)</span>
        </Label>
        <Input id="accountNum" {...register('accountNum')} />
      </div>
      <div>
        <Label htmlFor="notes">
          Anything else? <span className="text-neutral-400">(optional)</span>
        </Label>
        <textarea
          id="notes"
          rows={3}
          {...register('notes')}
          className="mt-1 block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 focus:outline-none"
        />
      </div>

      {captchaRequired && (
        <div className="pt-1">
          <TurnstileWidget siteKey={turnstileSiteKey} onToken={setCaptchaToken} />
        </div>
      )}

      {serverError && <p className="text-sm text-red-600">{serverError}</p>}
      <Button type="submit" className="w-full" disabled={submitDisabled}>
        {isSubmitting
          ? 'Sending…'
          : submitFailed
            ? 'Refresh the page to try again'
            : captchaRequired && !captchaToken
              ? 'Complete the challenge above'
              : 'Request account'}
      </Button>
    </form>
  );
}
